import { Box, Typography } from "@mui/material";
import { Loading, PlanConfirmationCard } from "../../../components";
import {
  MODALS,
  OVERDUE_STATUSES,
  PLAN_CONFIRMATION_DISMISSED_KEY,
  WELLNESS_PLAN_STATUSES,
  WELLNESS_PLAN_TYPES,
} from "../../../config/params";
/* eslint-disable max-len */
import React, { useEffect, useState } from "react";

import { CATEGORIES_LIST } from "../PetCareContainer/CategoriesPanel";
import { IPetItemResponse } from "../../../services/pet-list/pet-list.interface";
import { IWellnessPlanBenefitUsage } from "../../../services/wellness-plan/wellness-plan.interface";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import NullGeneric from "../../../components/NullGeneric/NullGeneric";
import { PAGES_PATH } from "../../../navigation/params";
import WellnessMemberContainer from "../WellnessMemberContainer/WellnessMemberContainer";
import WellnessMemberContainerOld from "../WellnessMemberContainer/WellnessMemberContainerOld";
import { useAllServiceStatusStore } from "../../../hooks";
import { useFetchWellnessPlanByPetId } from "../../../services/wellness-plan/wellness-plan.service";
import { useModal } from "../../../context/ModalContext";
import { useOverdueInvoiceStore } from "../../../hooks/redux-hooks/overdue-invoice-store/overdue-invoice.store";
import { useSelector } from "react-redux";
import { useWellnessFunnel } from "../../../hooks/useWellnessFunnel";
import { userActivityLogger } from "../../../utils/mixpanel";

interface IWellnessPlanContainer {
  petData: IPetItemResponse;
  petList: IPetItemResponse[];
  handlePetChange: (petId: string, category?: string) => void;
  hospitalDetailsContentful?: any;
  handleSelectCategory: (type: string) => void;
}

const WellnessPlanContainer: React.FC<IWellnessPlanContainer> = ({
  petData,
  petList,
  handlePetChange,
  hospitalDetailsContentful,
  handleSelectCategory,
}) => {
  const petCare = useSelector((state: any) => state.carePage.petCareData);

  const getDismissedKey = (petId: string) => `${PLAN_CONFIRMATION_DISMISSED_KEY}-${petId}`;
  const isDismissed = () => localStorage.getItem(getDismissedKey(petData.id)) === "true";
  const [showConfirmationCard, setShowConfirmationCard] = useState<boolean>(!isDismissed());
  const [wellnessUsageData, setWellnessUsageData] = useState<
    IWellnessPlanBenefitUsage[]
  >([]);
  const { getWellnessPlanByPetId, data, loading, error } =
    useFetchWellnessPlanByPetId();
  const { cancelledWellnessPetList, pausedWellnessPetList } =
    useOverdueInvoiceStore();
  const { openModal } = useModal();
  const wellnessFunnel = useWellnessFunnel({ source: "wellness_page" });
  const getPetOverdueStatus = (
    planStatus: string,
    petId: string,
    isScratchpay?: boolean,
    isOverdue?: boolean,
  ) => {
    const normalizedStatus = planStatus.toLowerCase();
    const isScratchpayLocked = Boolean(isScratchpay) && normalizedStatus === WELLNESS_PLAN_STATUSES.LOCKED;
    if (isScratchpayLocked) return OVERDUE_STATUSES.PAUSED;
    if (isScratchpay && isOverdue) return OVERDUE_STATUSES.OVERDUE;

    switch (normalizedStatus) {
    case WELLNESS_PLAN_STATUSES.PAUSED:
    case WELLNESS_PLAN_STATUSES.LOCKED:
      if (isScratchpay) {
        return OVERDUE_STATUSES.PAUSED;
      }
      return pausedWellnessPetList.some((petData) => petData.petId === petId) ?
        OVERDUE_STATUSES.PAUSED :
        null;
    case WELLNESS_PLAN_STATUSES.CANCELLED:
      if (isScratchpay) {
        return OVERDUE_STATUSES.CANCELLED;
      }
      return cancelledWellnessPetList.some((petData) => petData.petId === petId) ?
        OVERDUE_STATUSES.CANCELLED :
        null;
    default:
      return null;
    }
  };

  const { isWellnessServiceAvailable } = useAllServiceStatusStore();
  const otherPets = !petData.species || petData.species === "3";

  const handleSubscribeClick = () => {
    if (!petData) return;
    wellnessFunnel.selectPet(petData);
  };

  const getPlanType = (planType: string | undefined) => {
    if (!planType) return "";
    switch (planType.toLowerCase()) {
    case WELLNESS_PLAN_TYPES.BASE:
      return "TotalCare Base Plan";
    case WELLNESS_PLAN_TYPES.PREMIUM:
      return "TotalCare Plan";
    case WELLNESS_PLAN_TYPES.COMPREHENSIVE:
      return "TotalCare Surgery Plan";
    default:
      return "";
    }
  };

  useEffect(() => {
    if (!petData) return;
    setShowConfirmationCard(!isDismissed());
    getWellnessPlanByPetId(petData.id);
  }, [petData?.id]);

  useEffect(() => {
    if (data) {
      data?.wellnessplanBenefitUsage &&
        setWellnessUsageData(data.wellnessplanBenefitUsage);
    } else if (error) {
      // Reset state on error to prevent stale data
      setWellnessUsageData([]);
      console.error("Error fetching wellness plan:", error);
    }
  }, [data, error]);

  useEffect(() => {
    if (petData.id) {
      if (!petCare?.pets) return;
      const currentPetData = petCare.pets.filter(
        (pets: any) => pets?.id == petData.id,
      );
      if (!currentPetData[0]) return;
      const isHavingWellnessPlan = currentPetData[0].items.wellnessPlanStatus;
      const isOverdue = currentPetData[0].socStatus;

      if (data) {
        if (
          data.wellnessPlanPaymentStatus &&
          data.wellnessPlanPaymentStatus !== WELLNESS_PLAN_STATUSES.ACTIVE
        ) {
          userActivityLogger(MIXPANEL_EVENTS.VISIT_WELLNESS_PLAN, {
            wellness_subscriber: isHavingWellnessPlan ? "yes" : "no",
            pet_overdue: isOverdue == "Overdue" ? "yes" : "no",
            referred_from_page: "care_portal",
            invoice_overdue: "yes",
            no_of_overdue_invoices: data.pendingSquareInvoices.length,
          });
        } else {
          userActivityLogger(MIXPANEL_EVENTS.VISIT_WELLNESS_PLAN, {
            wellness_subscriber: isHavingWellnessPlan ? "yes" : "no",
            pet_overdue: isOverdue == "Overdue" ? "yes" : "no",
            referred_from_page: "care_portal",
            invoice_overdue: "no",
          });
        }
      }
    }
  }, [petData.id, data]);

  useEffect(() => {
    const wellnessRedirect = JSON.parse(
      localStorage.getItem("wellness-utm") || "{}",
    );

    if (wellnessRedirect.isWellnessPageRedirect) {
      localStorage.setItem(
        "wellness-utm",
        JSON.stringify({ ...wellnessRedirect, isWellnessPageRedirect: false }),
      );
    }
  }, []);

  // SGPD-400: the Care/Pet TotalCare tab is an entry point — the user must
  // see the "You don't have TotalCare Plan" empty state and click
  // "Explore Plans" to enter the funnel. We no longer auto-fire the
  // subscribe handler on mount; this hook now only surfaces the
  // delinquency alerts that need to take precedence over the entry point.
  const checkDelinquencyOnMount = () => {
    if (
      isWellnessServiceAvailable &&
      Object?.keys(hospitalDetailsContentful).length
    ) {
      if (!data?.isSubscriptionActive && !otherPets) {
        if (cancelledWellnessPetList.length > 0) {
          handleSelectCategory(CATEGORIES_LIST[0].type);
          openModal(MODALS.PENDING_INVOICE_CANCELLED_ALERT_MODAL);

          return;
        }
        if (pausedWellnessPetList.length > 0) {
          handleSelectCategory(CATEGORIES_LIST[0].type);
          openModal(
            MODALS.PENDING_INVOICE_PAUSED_ALERT_MODAL,
            PAGES_PATH.PET_CARE,
          );
        }
      }
    }
  };

  useEffect(() => {
    if (
      data &&
      isWellnessServiceAvailable &&
      petData &&
      hospitalDetailsContentful
    ) {
      checkDelinquencyOnMount();
    }
  }, [hospitalDetailsContentful, petData, isWellnessServiceAvailable, data]);

  if (loading) {
    return (
      <div className="h-2/4 flex justify-center items-center">
        <Loading />
      </div>
    );
  }

  const planType = getPlanType(data?.planType ?? data?.wellnessPlan?.[0]?.planType);
  const examsCount =
    data?.wellnessplanBenefitUsage.filter(
      (usage) => usage.wellnessplanBenefitName === "Examinations",
    )[0]?.used || 0;

  const isActiveMember = data?.isSubscriptionActive;
  const hasBenefitUsage = (data?.wellnessplanBenefitUsage?.length ?? 0) > 0;

  const getOverdueProps = () => ({
    planOverdueStatus: getPetOverdueStatus(
      data!.wellnessPlanPaymentStatus,
      data!.pet.id,
      data?.isScratchpay || false,
      data?.isOverdue || false,
    ),
    pendingInvoices: data?.pendingSquareInvoices ?? [],
    paymentUrl:
      data?.pendingSquareInvoices?.length ?
        data.pendingSquareInvoices[0].publicUrl :
        "",
  });

  const renderActiveMember = () => {
    const overdueProps = getOverdueProps();

    if (planType) {
      return (
        <WellnessMemberContainer
          petList={petList}
          handlePetChange={handlePetChange}
          petData={petData}
          benifitUsageData={wellnessUsageData}
          {...overdueProps}
          totalSavings={data?.wellnessPlanDetails[0]?.totalSavings || 0}
          hasWellnessPlan={!!data?.wellnessPlanDetails[0]}
          planName={data!.planName}
          examsCount={examsCount}
          isScratchpay={data?.isScratchpay || false}
        />
      );
    }

    return (
      <WellnessMemberContainerOld
        petList={petList}
        handlePetChange={handlePetChange}
        petData={petData}
        benifitUsageData={wellnessUsageData}
        {...overdueProps}
      />
    );
  };

  const renderPendingBenefits = () => (
    <Box className="flex flex-col h-full" sx={{ bgcolor: "#F4EFEB" }}>
      {showConfirmationCard && (
        <Box className="w-full px-4 py-6">
          <PlanConfirmationCard
            petName={petData.name}
            petImage={petData.image}
            planName={data!.planName}
            onDismiss={() => {
              localStorage.setItem(getDismissedKey(petData.id), "true");
              setShowConfirmationCard(false);
            }}
          />
        </Box>
      )}
      <Box className="flex flex-col items-center text-center mt-16">
        <Typography
          sx={{
            fontFamily: "geograph-medium, sans-serif",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "20px",
            color: "black.main",
          }}
        >
          Hang Tight!
        </Typography>
        <Typography
          sx={{
            fontFamily: "geograph-regular, sans-serif",
            fontWeight: 400,
            fontSize: "12px",
            lineHeight: "18px",
            color: "black.main",
            mt: "4px",
            maxWidth: "283px",
          }}
        >
          Your pet’s plan information is on its way and
          it will be ready in a few moments. If plan is not visible in
          the next 24 hours, kindly reach out to our Support Team for help.
        </Typography>
        <Typography
          onClick={() => openModal(MODALS.CUSTOMER_SUPPORT_MODAL)}
          sx={{
            fontFamily: "geograph-medium, sans-serif",
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "14px",
            color: "black.main",
            mt: "16px",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Reach Out to Customer Support
        </Typography>
      </Box>
    </Box>
  );

  const renderNonMember = () => (
    <div className="flex flex-col gap-3 h-full">
      <div className="h-full flex-grow flex flex-col justify-center items-center max-h-[50vh] 4xl:max-h-[35vh]">
        {isWellnessServiceAvailable && petData.species ? (
          otherPets ? (
            <NullGeneric
              title="Wellness Plan Unavailable"
              description={`Wellness plans are only available for dogs and cats.
              We're continually expanding our services. Check back for updates!`}
              image="/assets/icons/wellness_plan_unavailable.png"
              showButton={false}
            />
          ) : (
            <div className="flex flex-col items-center text-center gap-3 max-w-md">
              <img
                src="/assets/icons/WellnessPlan_PurpleBG.png"
                alt="TotalCare Plan"
                className="w-32 h-32"
              />
              <Typography variant="h6" className="font-semibold">
                You don&apos;t have TotalCare Plan
              </Typography>
              <Typography variant="body2" className="text-light-black px-2">
                Don&apos;t wait, explore comprehensive, vet-designed plans
                to keep your pet&apos;s care on track.
              </Typography>
              <button
                type="button"
                onClick={handleSubscribeClick}
                className="text-sm text-center w-full bg-violet-3 p-2.5 rounded-2xl font-semibold hover:bg-gray-3 cursor-pointer mt-1"
              >
                Explore Plans
              </button>
            </div>
          )
        ) : (
          <NullGeneric
            title={
              otherPets && petData.species ?
                "Wellness Plan Unavailable" :
                "Service temporarily unavailable!"
            }
            description={
              otherPets && petData.species ? (
                `Wellness plans are only available for dogs and cats.
                We're upgrading the system for a smoother experience.Please stay tuned!`
              ) : (
                <>
                  We&apos;re upgrading the system for a smoother experience.
                  Please stay tuned!{" "}
                </>
              )
            }
            image={`/assets/icons/${
              otherPets ?
                "wellness_plan_unavailable.png" :
                "service-down.png"
            }`}
            handleCTA={handleSubscribeClick}
            buttonText="Explore Plans"
            showButton={false}
          />
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    if (data && isActiveMember && hasBenefitUsage) {
      return renderActiveMember();
    }
    if (data && isActiveMember && !hasBenefitUsage) {
      return renderPendingBenefits();
    }
    return renderNonMember();
  };

  return <div className="h-full">{renderContent()}</div>;
};

export default WellnessPlanContainer;
